// NavigationDropDown.tsx
import React, { useEffect, useRef, useState, useId, useCallback } from 'react';
import classNames from 'classnames';
import styles from '../Navigation.module.scss';
import { Icon } from 'ui/components';
import ChevronIconNav from 'ui/icons/icon-chevron-nav.svg';

export interface NavigationDropDownProps {
	trigger: React.ReactNode;
	content?: React.ReactNode;
	className?: string;

	isOpen?: boolean;
	defaultOpen?: boolean;
	onOpenChange?: (open: boolean) => void;

	closeOnOutsideClick?: boolean;
	closeOnEsc?: boolean;

	closeOnLinkClick?: boolean;
}

export const NavigationDropDown: React.FC<NavigationDropDownProps> = ({
	trigger,
	content,
	className,
	isOpen,
	defaultOpen = false,
	onOpenChange,
	closeOnOutsideClick = true,
	closeOnEsc = true,
	closeOnLinkClick = true,
}) => {
	const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(defaultOpen);
	const isExpanded = isOpen ?? uncontrolledIsOpen;

	const updateOpenState = (nextOpen: boolean) => {
		if (isOpen === undefined) setUncontrolledIsOpen(nextOpen);
		onOpenChange?.(nextOpen);
	};

	const dropdownItemRef = useRef<HTMLLIElement | null>(null);
	const contentRef = useRef<HTMLDivElement | null>(null);
	const contentAriaId = useId();

	useEffect(() => {
		if (!closeOnOutsideClick || !isExpanded) return;

		const handleDocumentPointerDown = (event: PointerEvent) => {
			const dropdownRoot = dropdownItemRef.current;
			if (!dropdownRoot) return;

			const eventTargetNode = event.target as Node | null;
			if (eventTargetNode && dropdownRoot.contains(eventTargetNode)) return;

			updateOpenState(false);
		};

		document.addEventListener('pointerdown', handleDocumentPointerDown, true);
		return () => document.removeEventListener('pointerdown', handleDocumentPointerDown, true);
	}, [isExpanded, closeOnOutsideClick]);

	// Close on ESC
	useEffect(() => {
		if (!closeOnEsc || !isExpanded) return;

		const handleKeyDown = (keyboardEvent: KeyboardEvent) => {
			if (keyboardEvent.key === 'Escape') updateOpenState(false);
		};

		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [isExpanded, closeOnEsc]);

	const handleContentClick = useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			e.stopPropagation();

			if (!closeOnLinkClick) return;

			const target = e.target as Element | null;
			const clickable = target?.closest?.('a,[role="link"],[data-close-on-click]');
			if (clickable) {
				requestAnimationFrame(() => updateOpenState(false));
			}
		},
		[closeOnLinkClick],
	);

	return (
		<li
			ref={dropdownItemRef}
			className={classNames(styles.Navigation_item, className, { [styles.isActive]: isExpanded })}
			aria-expanded={isExpanded}
			aria-haspopup={content ? 'dialog' : undefined}
			aria-controls={content ? contentAriaId : undefined}
		>
			<div
				className={styles.Navigation_trigger}
				role="button"
				tabIndex={0}
				onClick={(e) => {
					e.stopPropagation();
					updateOpenState(!isExpanded);
				}}
				onKeyDown={(keyboardEvent) => {
					if (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ') {
						keyboardEvent.preventDefault();
						keyboardEvent.stopPropagation();
						updateOpenState(!isExpanded);
					}
				}}
			>
				{trigger}
				<Icon size="lg" className={classNames(styles.Navigation_buttonIcon, { [styles.isActive]: isExpanded })}>
					<ChevronIconNav />
				</Icon>
			</div>

			{isExpanded && content ? (
				<div
					id={contentAriaId}
					ref={contentRef}
					className={styles.Navigation_content}
					role="dialog"
					aria-modal="false"
					onClick={handleContentClick}
					onPointerDown={(e) => e.stopPropagation()}
				>
					{content}
				</div>
			) : null}
		</li>
	);
};
